import React, { useState, useRef, useEffect } from "react";
import { X, Plus } from "lucide-react";

const SUGGESTIONS = [
  "rice", "ghee", "cumin", "ginger", "turmeric", "lentils", "spinach",
  "coconut milk", "coriander", "fennel", "cardamom", "cinnamon", "black pepper",
  "sesame oil", "chickpeas", "sweet potato", "oats", "milk", "honey",
];

export default function IngredientInput({ ingredients, setIngredients }) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const addIngredient = (value) => {
    const cleaned = value.trim().toLowerCase();
    if (!cleaned || ingredients.includes(cleaned)) {
      setInputValue("");
      return;
    }
    setIngredients([...ingredients, cleaned]);
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeIngredient = (item) => {
    setIngredients(ingredients.filter((i) => i !== item));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addIngredient(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && ingredients.length > 0) {
      setIngredients(ingredients.slice(0, -1));
    }
  };

  const filteredSuggestions = SUGGESTIONS.filter(
    (s) => s.includes(inputValue.toLowerCase()) && !ingredients.includes(s) && inputValue.length > 0
  ).slice(0, 6);

  useEffect(() => {
    setShowSuggestions(filteredSuggestions.length > 0 && inputValue.length > 0);
  }, [inputValue, filteredSuggestions.length]);

  return (
    <div className="relative">
      {/* Tag container */}
      <div
        className="min-h-[56px] w-full rounded-xl px-3 py-2 flex flex-wrap gap-2 items-start cursor-text transition-all"
        style={{
          backgroundColor: "#FDFBF7",
          border: "2px solid #E5E0D8",
          fontFamily: "Manrope, sans-serif",
        }}
        onClick={() => inputRef.current?.focus()}
        data-testid="ingredient-container"
      >
        {/* Tags */}
        {ingredients.map((item) => (
          <span
            key={item}
            className="ingredient-tag inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
            style={{ backgroundColor: "#F2CC8F33", color: "#8B6914", border: "1px solid #F2CC8F80" }}
          >
            {item}
            <button
              data-testid={`remove-ingredient-${item}`}
              onClick={(e) => { e.stopPropagation(); removeIngredient(item); }}
              className="hover:opacity-70 transition-opacity"
            >
              <X size={12} />
            </button>
          </span>
        ))}

        {/* Input */}
        <input
          ref={inputRef}
          data-testid="ingredient-input"
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (inputValue.trim()) addIngredient(inputValue);
            setTimeout(() => setShowSuggestions(false), 150);
          }}
          placeholder={ingredients.length === 0 ? "Type an ingredient and press Enter..." : "Add more..."}
          className="flex-1 min-w-[160px] bg-transparent border-none outline-none text-base py-1"
          style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}
        />

        {/* Add button */}
        {inputValue.trim() && (
          <button
            data-testid="add-ingredient-btn"
            onClick={() => addIngredient(inputValue)}
            className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-all hover:scale-105"
            style={{ backgroundColor: "#E07A5F", color: "#FFFFFF" }}
          >
            <Plus size={12} />
            Add
          </button>
        )}
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && (
        <div
          className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-card z-10 overflow-hidden"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8" }}
        >
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              data-testid={`suggestion-${s}`}
              onMouseDown={() => addIngredient(s)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted transition-colors"
              style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Quick add popular — show ones not yet added */}
      {["rice", "ghee", "cumin", "ginger", "lentils"].filter(s => !ingredients.includes(s)).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>Quick add:</span>
          {["rice", "ghee", "cumin", "ginger", "lentils"].filter(s => !ingredients.includes(s)).map((s) => (
            <button
              key={s}
              data-testid={`quick-add-${s}`}
              onClick={() => addIngredient(s)}
              className="text-xs px-3 py-1 rounded-full transition-all hover:scale-105"
              style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
